/* Import dependencies */
import React from 'react';
import moment from 'moment';
import data from '../core/db/main.json';
import LightboxWrapper from './lightbox-wrapper';
import SplashPage from './splashpage';
import SliverBanner from './sliverbanner';
import RollupBox from './rollupbox';
import ExpandedBanner from './expandedbanner';
import { MultiSliverBanner } from './multisliverbanner';

/** State Interface */
interface IState {
  components: any;
  isTest: boolean;
}

/** Component */
class App extends React.Component {
  /** Init state */
  state: IState = { components: [], isTest: false };

  public constructor(props: IState) {
    super(props);
    this.searchByDate = this.searchByDate.bind(this);
    this.getParam = this.getParam.bind(this);
    this.isTest = this.isTest.bind(this);
    this.searchByTestKey = this.searchByTestKey.bind(this);
    this.setSession = this.setSession.bind(this);
    this.hasSession = this.hasSession.bind(this);
    this.renderComponent = this.renderComponent.bind(this);
    this.validateLocation = this.validateLocation.bind(this);
  }

  public componentDidMount() {
    /** Let it pass if it is a test */
    if (this.isTest()) {
      return;
    }

    /** Filter Components and update the state */
    this.setState({
      components: data.data.filter(this.searchByDate)
    });
  }

  /** has session method */
  public hasSession(sessionName: string): boolean {
    const cname = sessionName;
    const name = cname + '=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let item of ca) {
      while (item.charAt(0) === ' ') {
        item = item.substring(1);
      }
      if (item.indexOf(name) === 0) {
        return item.substring(name.length, item.length) === 'true' ? true : false;
      }
    }
    return false;
  }

  /** set session method */
  public setSession(sessionName: string): void {
    const name = sessionName;
    const time = new Date();
    const timeForm = name === 'pseventDay25' ? 2 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000; // If it is ps event day set session for 2hrs otherwise 24hrs
    time.setTime(time.getTime() + 1 * timeForm);
    const expires = 'expires=' + time.toUTCString();
    document.cookie = name + '=' + true + ';' + expires + ';path=/';
  }

  /** get window params method */
  public getParam(name: string): string {
    const url = window.location.href;
    name = name.replace(/[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
    const results = regex.exec(url);
    if (!results) {
      return '';
    }

    if (!results[2]) {
      return '';
    }

    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }

  /** Is a test method */
  public isTest(): boolean {
    // const key = this.getParam('test');
    const params = new URLSearchParams(window.location.search);
    const key: string = params.get('test') || '';
    const results = data.data.filter((item: any) => this.searchByTestKey(item, key));
    const tester: boolean = results.length > 0 ? true : false;
    this.setState({ components: results, isTest: tester });

    return results.length > 0 ? true : false;
  }

  /** Helper method to filter components by test key */
  public searchByTestKey(item: any, key: string): any {
    return item.testKey === key;
  }

  /** Helper method to filter components by date */
  public searchByDate(item: any): any {
    const today = moment();
    if (today >= moment(item.startDate) && today < moment(item.endDate)) {
      return item;
    }
  }

  /** Validate window location method */
  public validateLocation(component: any): boolean {
    const normalize = (path: string) => path.replace(/\/+$/, ''); // remove trailing slashes
    const currentPath = normalize(document.location.pathname);

    // Global always allowed
    if (component.location === 'global' && !component.hasOwnProperty('exceptions')) {
      return true;
    }

    // Check exceptions
    if (component.exceptions?.length) {
      const isException = component.exceptions.some((ex: string) => normalize(ex) === currentPath);
      if (isException) return false;
    }

    // Check allowed locations
    if (Array.isArray(component.location)) {
      return component.location.some((loc: string) => {
        loc = normalize(loc);

        if (loc.endsWith('/*')) {
          const base = loc.slice(0, -2); // remove the /* at the end
          return currentPath === base || currentPath.startsWith(`${base}/`);
        }

        // exact match
        return currentPath === loc;
      });
    }

    return false;
  }

  /** Render the component, this only understand one component for now */
  public renderComponent(component: any, key: number): any {
    switch (component.type) {
      case 'lightbox':
        /** It is not a test */
        if (this.state.isTest) {
          console.log(`Component loaded test: ${this.state.isTest}`);
          return <LightboxWrapper data={component} key={key} />;
        }
        /** Make sure the component was meant to be here */
        if (!this.validateLocation(component)) {
          return console.log(`Direct Response Component ${component.type} will not be mounted in this location`);
        }

        /** Check for session */
        if (this.hasSession(component.sessionName)) {
          return console.log(`Direct Response Component ${component.type} has session, and will not be mounted`);
        }

        this.setSession(component.sessionName);
        console.log(`Component ${component.type} set, with this specific rules ${component.location}`);

        return <LightboxWrapper data={component} key={key} />;

      case 'splashpage':
        /** Make sure the component was meant to be here */
        if (!this.validateLocation(component)) {
          return console.log(`Direct Resonse Component ${component.type} will not be mounted in this location`);
        }

        if (this.hasSession(component.sessionName) && !this.state.isTest) {
          return console.log(`Direct Response Component ${component.type} has session, and will not be mounted`);
        }

        this.setSession(component.sessionName);
        console.log(`Direct Response Component ${component.type} set.`);
        return <SplashPage data={component} show={'open'} key={key} />;

      case 'rollupbox':
        /** Make sure the component was meant to be here */
        if (!this.validateLocation(component)) {
          return console.log(`Direct Resonse Component ${component.type} will not be mounted in this location`);
        }

        if (this.hasSession(component.sessionName) && !this.state.isTest) {
          return console.log(`Direct Response Component ${component.type} has session, and will not be mounted`);
        }

        this.setSession(component.sessionName);
        console.log(`Direct Response Component ${component.type} set.`);
        return <RollupBox data={component} key={key} />;

      case 'sliverbanner':
        /** Sliver banners usually are applied globally */
        // if (!this.validateLocation(component)) {
        //   return console.log(`Direct Resonse Component ${component.type} will not be mounted in this location`);
        // }

        this.setSession(component.sessionName);
        console.log(`Direct Response Component ${component.type} set.`);
        return <SliverBanner data={component} key={key} />;

      case 'multisliverbanner':
        this.setSession(component.sessionName);
        console.log(`Direct Response Component ${component.type} set.`);
        return <MultiSliverBanner data={component} key={key} />;

      case 'expandedbanner':
        if (this.state.isTest) {
          console.log(`Component loaded test: ${this.state.isTest}`);
          return <ExpandedBanner data={component} key={key} />;
        }
        if (!this.validateLocation(component)) {
          return console.log(`Direct Resonse Component ${component.type} will not be mounted in this location`);
        }

        console.log(`Direct Response Component ${component.type} set.`);
        return <ExpandedBanner data={component} key={key} />;
      default:
        return console.log('empty', 'No Direct Response module scheduled.');
    }
  }

  public render() {
    const { components } = this.state;
    return <div className="App">{components.map((component: any, key: number) => this.renderComponent(component, key))}</div>;
  }
}

export default App;
